import Image from '@/models/image.model';
import Note from '@/models/note.model';
import Tag from '@/models/tag.model';

import db from '@/database';

import BaseStorageProvider from './base.provider';

export default class DexieProvider extends BaseStorageProvider {
  constructor(store) {
    super();

    this.$store = store;

    this.$store.commit('setStorageProviderReady', true);
  }

  async getNotes({
    offset = 0,
    limit = 10,
    tag = null,
    search = null,
    untagged = false,
    archived = false,
    deleted = false,
    uuidsOnly = false,
  } = {}) {
    if (tag && untagged) {
      throw new Error("'tag' and 'untagged' can't be specified at the same time");
    }

    if (archived && deleted) {
      throw new Error("'archived' and 'deleted' can't be specified at the same time");
    }

    if (offset === undefined) {
      offset = 0;
    }

    if (limit === undefined) {
      limit = 10;
    }

    let collection = db.notes;

    if (tag !== null) {
      collection = collection.where('tags').equals(tag);
    }

    if (untagged) {
      collection = collection.filter(note => note.tags.length === 0);
    }

    collection = collection.filter(note => note.archived === archived && note.deleted === deleted);

    if (search !== null) {
      collection = collection.filter(note => {
        const text = Note.getNodeText(note.content).toLowerCase();
        return text.includes(search.toLowerCase());
      });
    }

    collection = collection.reverse().offset(offset);

    if (limit !== 0) {
      collection = collection.limit(limit);
    }

    if (uuidsOnly) {
      return collection.primaryKeys();
    }

    return collection.sortBy('updated');
  }

  async getNote(uuid) {
    return db.notes.get(uuid);
  }

  async createNote() {
    const note = new Note();

    await db.notes.add(note);

    return note;
  }

  async updateNote(note) {
    await db.notes.put(note);
  }

  async deleteNote(uuid) {
    const note = await this.getNote(uuid);
    await db.notes.delete(uuid);

    const tagDeletions = [];
    for (const tag of note.tags) {
      tagDeletions.push(this.deleteTag(tag));
    }
    await Promise.all(tagDeletions);
  }

  async restoreDeletedNotes() {
    db.notes.toCollection().modify({ deleted: false });
  }

  async purgeDeletedNotes() {
    await db.notes.filter(note => note.deleted).delete();
  }

  async getStagedNotes() {
    await db.staging.toArray();
  }

  async stageNote(note) {
    await db.staging.put(note);
  }

  async clearStagedNotes() {
    await db.staging.clear();
  }

  async deleteStagedNote(uuid) {
    await db.staging.delete(uuid);
  }

  async getNotesRaw() {
    return db.notes.toArray();
  }

  async setNotesRaw(notes) {
    await db.notes.clear();
    db.notes.bulkPut(notes);
  }

  async getTags() {
    return db.tags.orderBy('name').toArray();
  }

  async getTag(name) {
    return db.tags.get(name);
  }

  async createTag(name) {
    const tagExists = await db.tags.get(name);

    if (tagExists) {
      return;
    }

    await db.tags.add(new Tag({ name }));
  }

  async updateTag(tag) {
    await db.tags.put(tag);
  }

  async deleteTag(name) {
    const count = await db.notes.where('tags').equals(name).count();

    if (count !== 0) {
      return;
    }

    await db.tags.delete(name);
  }

  async getTagsRaw() {
    return db.tags.toArray();
  }

  async setTagsRaw(tags) {
    await db.tags.clear();
    db.tags.bulkPut(tags);
  }

  async getImages({
    noteUuid = null,
    uuidsOnly = false,
  } = {}) {
    let { images } = db;
    if (noteUuid) {
      images = images.where('noteUuid').equals(noteUuid);
    }

    if (uuidsOnly) {
      return images.primaryKeys();
    }

    return images.toArray();
  }

  async getImage(uuid) {
    return db.images.get(uuid);
  }

  async createImage(noteUuid, data, uuid) {
    const image = new Image({
      uuid,
      noteUuid,
      data,
    });

    await db.images.add(image);

    return image;
  }

  async deleteNoteImages(noteUuid) {
    await db.images.where('noteUuid').equals(noteUuid).delete();
  }

  async deleteImage(uuid) {
    await db.images.delete(uuid);
  }

  async getImagesRaw() {
    return db.images.toArray();
  }

  async setImagesRaw(images) {
    await db.images.clear();
    db.images.bulkPut(images);
  }
}
